/**
 * «Источники» на экране «Лиды»: откуда пришли лиды периода. Сайт раскрыт по
 * заявкам — виджеты попапа, баннеры каталога, заказы (у лида в Bitrix это всё
 * `WEB`); остальное — звонки, открытые линии, формы Bitrix. Разрез «по рекламе» —
 * метка `advertID`. Спека `docs/specs/2026-09-30-leads-sources-design.md`;
 * числа — те же, что у `prokat-runner leads-sources`.
 */
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router";

import { DateRangePicker } from "@/components/DateRangePicker";
import { iso, plusDays } from "@/lib/dates";
import {
  type LeadSources,
  type SourceCounts,
  type SourceRow,
  type WebCounts,
  loadLeadSources,
  sourceChats,
} from "@/lib/leadSources";
import { LeadSourcesDaysSection } from "@/pages/LeadSourcesDays";

type View = "channel" | "advert";

type WebCol = { key: keyof WebCounts; head: string; hint: string; of?: keyof WebCounts; tone?: string };
const WEB_COLS: WebCol[] = [
  { key: "submissions", head: "Заявок", hint: "заявки сайта, поданные в периоде: попап, баннер, заказ" },
  {
    key: "dropped",
    head: "Отсеяно",
    hint: "попап: спам (≥ 5 номеров с одного IP/гостя за сутки) или нет контактов; баннер: тестовый номер",
    of: "submissions",
    tone: "text-err",
  },
  { key: "to_lead", head: "В лид", hint: "у заявки есть лид Bitrix", of: "submissions" },
  {
    key: "to_old_lead",
    head: "В старый лид",
    hint: "лид создан раньше заявки больше чем на 10 мин: у номера уже был лид в NEW, заявку дописали в него",
    of: "to_lead",
  },
];

type Col = { key: keyof SourceCounts; head: string; hint: string; of?: keyof SourceCounts; tone?: string };
const COLS: Col[] = [
  { key: "leads", head: "Лидов", hint: "лиды, созданные в периоде, у которых это первый источник" },
  { key: "phones", head: "Уникальных номеров", hint: "рабочий номер, без мусора, номер в строке — один раз" },
  { key: "new", head: "Новых", hint: "по номеру за 90 дней до лида лидов не было", of: "phones" },
  { key: "deal", head: "Сделка", hint: "у лида номера есть сделка", of: "phones" },
  { key: "recorded", head: "Записали", hint: "сделка входила в «ждем клиента» после создания лида", of: "phones", tone: "text-ok" },
  { key: "booked", head: "Бронь", hint: "договор сделки → «Бронь»/«Подписан» после создания лида", of: "phones", tone: "text-ok" },
];

function pct(a: number, b: number): string {
  return b ? `${Math.round((a * 100) / b)}%` : "";
}

/** Число ячейки; ненулевое — ссылка: клиенты за ним в «Чатах». */
function Num({ v, of, tone, sep, to }: { v: number; of?: number | undefined; tone?: string | undefined; sep?: boolean; to: string }) {
  const cls = `text-[14px] font-semibold ${v ? (tone ?? "text-fg") : "text-fg-faint"}`;
  return (
    <td className={`whitespace-nowrap px-3 py-1.5 tabular-nums ${sep ? "border-r border-border" : ""}`}>
      {v ? (
        <Link to={to} title="открыть чаты этих клиентов" className={`${cls} hover:underline`}>
          {v}
        </Link>
      ) : (
        <span className={cls}>{v}</span>
      )}
      {of !== undefined && v > 0 && <span className="ml-1.5 text-[12px] text-fg-muted">{pct(v, of)}</span>}
    </td>
  );
}

/** Числа строки; `chats` — ссылка на «Чаты» по колонке (строка уже выбрана). */
function Cells({ r, web, chats }: { r: SourceCounts; web: WebCounts | null | false; chats: (col: string) => string }) {
  return (
    <>
      {web !== false &&
        WEB_COLS.map((c, i) =>
          web ? (
            <Num key={c.key} v={web[c.key]} of={c.of && web[c.of]} tone={c.tone} sep={i === WEB_COLS.length - 1} to={chats(c.key)} />
          ) : (
            <td key={c.key} className={i === WEB_COLS.length - 1 ? "border-r border-border" : ""} />
          ),
        )}
      {COLS.map((c) => (
        <Num key={c.key} v={r[c.key]} of={c.of && r[c.of]} tone={c.tone} to={chats(c.key)} />
      ))}
    </>
  );
}

/** Ссылки строки `src` на «Чаты» за период таблицы. */
const linker = (r: LeadSources) => (src: string) => (col: string) => sourceChats(src, col, r.from, r.to);

function Head({ first, web }: { first: string; web: boolean }) {
  const th = "whitespace-nowrap px-3 py-2 font-medium";
  return (
    <thead className="text-left text-[11.5px] text-fg-faint">
      {web && (
        <tr>
          <th />
          <th colSpan={WEB_COLS.length} className={`${th} border-r border-border pb-0`}>
            Заявки сайта
          </th>
          <th colSpan={COLS.length} className={`${th} pb-0`}>
            Лиды
          </th>
        </tr>
      )}
      <tr>
        <th className={`${th} pl-4`}>{first}</th>
        {web &&
          WEB_COLS.map((c, i) => (
            <th key={c.key} className={`${th} ${i === WEB_COLS.length - 1 ? "border-r border-border" : ""}`} title={c.hint}>
              {c.head}
            </th>
          ))}
        {COLS.map((c) => (
          <th key={c.key} className={th} title={c.hint}>
            {c.head}
          </th>
        ))}
      </tr>
    </thead>
  );
}

function Channels({ r }: { r: LeadSources }) {
  const chats = linker(r);
  return (
    <table className="w-auto text-[12.5px]">
      <Head first="Источник" web />
      <tbody>
        {r.groups.map((g) => (
          <GroupRows key={g.key} g={g} chats={chats} />
        ))}
        <tr className="border-t-2 border-border font-medium" data-annot="итого источники">
          <td className="py-2 pl-4 pr-3 text-fg-muted">Итого</td>
          <Cells r={r.total} web={r.total.web} chats={chats("total")} />
        </tr>
      </tbody>
    </table>
  );
}

function GroupRows({ g, chats }: { g: LeadSources["groups"][number]; chats: ReturnType<typeof linker> }) {
  return (
    <>
      <tr className="border-t-2 border-border bg-bg" data-annot={`группа ${g.label}`}>
        <td className="whitespace-nowrap py-1.5 pl-4 pr-3 font-semibold">{g.label}</td>
        <Cells r={g} web={g.web} chats={chats(`g:${g.key}`)} />
      </tr>
      {g.children.map((c: SourceRow) => (
        <tr key={c.key} className="border-t border-border hover:bg-bg-hover" data-annot={`источник ${c.label}`}>
          <td className={`whitespace-nowrap py-1.5 pl-8 pr-3 ${c.leads ? "" : "text-fg-muted"}`}>{c.label}</td>
          <Cells r={c} web={c.web} chats={chats(`s:${c.key}`)} />
        </tr>
      ))}
    </>
  );
}

function Adverts({ r }: { r: LeadSources }) {
  const chats = linker(r);
  return (
    <table className="w-auto text-[12.5px]">
      <Head first="Реклама" web={false} />
      <tbody>
        {r.adverts.map((a) => (
          <tr key={a.key} className="border-t border-border hover:bg-bg-hover" data-annot={`реклама ${a.label}`}>
            <td className="whitespace-nowrap py-1.5 pl-4 pr-3">{a.label}</td>
            <Cells r={a} web={false} chats={chats(`a:${a.key}`)} />
          </tr>
        ))}
        <tr className="border-t-2 border-border font-medium" data-annot="итого реклама">
          <td className="py-2 pl-4 pr-3 text-fg-muted">Итого</td>
          <Cells r={r.total} web={false} chats={chats("total")} />
        </tr>
      </tbody>
    </table>
  );
}

const SUBS = [
  { id: "detail", label: "Детализация" },
  { id: "days", label: "По дням" },
] as const;

/** Подвкладки «Источников»: `&sub=days` — «По дням», иначе «Детализация». */
export function LeadSourcesSection() {
  const [params, setParams] = useSearchParams();
  const sub = params.get("sub") === "days" ? "days" : "detail";
  const select = (id: string) => {
    const out = new URLSearchParams(params);
    if (id === "days") out.set("sub", id);
    else out.delete("sub");
    out.delete("view");
    out.delete("metric");
    out.delete("group");
    setParams(out, { replace: true });
  };
  return (
    <>
      <div role="tablist" aria-label="Источники" className="flex gap-1" data-annot="подвкладки источников">
        {SUBS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={sub === t.id}
            onClick={() => select(t.id)}
            className={`h-7 rounded-[var(--radius-ui)] px-3 text-[12.5px] ${
              sub === t.id ? "bg-bg-hover font-medium text-fg" : "text-fg-muted hover:text-fg"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {sub === "days" ? <LeadSourcesDaysSection /> : <DetailSection />}
    </>
  );
}

function DetailSection() {
  const [params, setParams] = useSearchParams();
  const today = new Date();
  const from = params.get("from") || iso(plusDays(today, -29));
  const to = params.get("to") || iso(today);
  const view: View = params.get("view") === "advert" ? "advert" : "channel";
  const set = (patch: Record<string, string>) => {
    const out = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) out.set(k, v);
      else out.delete(k);
    }
    setParams(out, { replace: true });
  };
  const q = useQuery({ queryKey: ["leads-sources", from, to], queryFn: () => loadLeadSources(from, to) });
  const seg = (on: boolean) =>
    `h-7 rounded-[var(--radius-ui)] border px-2.5 text-[12.5px] ${
      on ? "border-accent text-fg" : "border-border text-fg-muted hover:text-fg"
    }`;
  const r = q.data;

  return (
    <>
      <div className="flex flex-wrap items-center gap-3" data-annot="период источники">
        <DateRangePicker label="Период" from={from} to={to} onChange={(a, b) => set({ from: a, to: b })} />
        <div className="flex gap-1" role="group" aria-label="Разрез">
          <button type="button" onClick={() => set({ view: "" })} aria-pressed={view === "channel"} className={seg(view === "channel")}>
            по каналам
          </button>
          <button type="button" onClick={() => set({ view: "advert" })} aria-pressed={view === "advert"} className={seg(view === "advert")}>
            по рекламе
          </button>
        </div>
      </div>
      {q.isPending && <div className="text-[12.5px] text-fg-muted">Считаю источники по CRM…</div>}
      {q.isError && <div className="text-[12.5px] text-err">Не удалось получить источники: {String(q.error.message)}</div>}
      {r && (
        <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot={`источники ${view}`}>
          <div className="overflow-x-auto">{view === "advert" ? <Adverts r={r} /> : <Channels r={r} />}</div>
          <div className="border-t border-border px-4 py-2 text-[11.5px] text-fg-faint">
            {view === "advert"
              ? "Реклама — метка лида «Откуда узнали» (advertID): сайт ставит её по utm_source или рефереру; «Без метки» — поле пустое. "
              : "Сайт в Bitrix — один источник WEB; здесь он раскрыт по заявкам: попап (виджеты и квизы), баннер каталога, заказ с сайта. " +
                "Лид относится к самой ранней заявке с его номером лида, иначе — к источнику Bitrix. Заявки считаются по дате заявки: " +
                "если у номера уже открыт лид в NEW, заявку дописывают в него («в старый лид»), новый не создаётся. "}
            Уникальные номера, сделка, записали и бронь — как во вкладке «По дням»; группа и итог — уникальные номера
            заново, не сумма строк. Проценты — от уникальных номеров, у заявок — от заявок. Число — ссылка: чаты этих
            клиентов (по номеру, лиду и сделке) в «Чатах».
          </div>
        </section>
      )}
    </>
  );
}
